import React from 'react';
import { FormGroup, List } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { Link } from '@s20/components/common/Link';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes, AppNavPaths } from '../../Common';
import { FormLabel, SmallButton, toasty } from '../../common/forms/FormElements';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import { BlockBorder } from '../../common/Block';
import { SaveButton } from '../../common/forms/SaveButton';
import { Upload } from '../ImportCustomerData/Upload';

const importOptions = [
    { value: 'ADP_EMPLOYEES', label: 'ADP Employees' },
    { value: 'CONTACTS', label: 'Contacts' },
    { value: 'CHARGE_TYPES', label: 'Charge Types' },
    { value: 'CUSTOMERS', label: 'Customers' },
    { value: 'EMPLOYEES', label: 'Employees' },
    { value: 'EQUIPMENTS', label: 'Equipment' },
    { value: 'INVENTORY_ITEMS', label: 'Inventory Items' },
    { value: 'POSITIONS', label: 'Positions' },
    { value: 'VEHICLES', label: 'Vehicles' },
];

const sampleDocuments = {
    ADP_EMPLOYEES: {
        fileName: 'adp-employees-import-sample.csv',
        headers: ['ROADS Id', 'ROADS FirstName', 'ROADS LastName', 'ROADS Email', 'ROADS Dispatch Office', 'ADP Associate Oid', 'ADP Worker Id', 'ADP Payroll File #', 'ADP First Name', 'ADP Last Name', 'ADP Email', 'ADP Dispatch Office', 'ADP Job Title', 'ADP Job Code'],
        row: ['123', 'Avery', 'Johnson', 'avery.johnson@example.com', 'Tampa Branch', 'AO123', 'W123', '1001', 'Avery', 'Johnson', 'avery.johnson@example.com', 'Tampa Branch', 'Flagger', 'FLG'],
    },
    CONTACTS: {
        fileName: 'contacts-import-sample.csv',
        headers: ['First Name', 'Last Name', 'Company Name', 'Phone', 'Email', 'Type'],
        row: ['Jordan', 'Smith', 'Acme Construction', '555-0102', 'jordan.smith@example.com', 'Billing'],
    },
    CHARGE_TYPES: {
        fileName: 'charge-types-import-sample.csv',
        headers: ['Name', 'Unit', 'Usage', 'ResourceTypeId'],
        row: ['Flagger Regular Time', '1', '1', '1'],
    },
    CUSTOMERS: {
        fileName: 'customers-import-sample.csv',
        headers: ['Name', 'Billing Address 1', 'Billing Address 2', 'Billing City', 'Billing State/Province', 'Billing Zip', 'Terms', 'NetSuite ID'],
        row: ['Acme Construction', '500 Market St', 'Suite 200', 'Tampa', 'FL', '33602', 'Net 30', '123456'],
    },
    EMPLOYEES: {
        fileName: 'employees-import-sample.csv',
        headers: ['Employee #', 'First Name', 'Last Name', 'Address Line 1', 'Address Line 2', 'City', 'State', 'Zip Code', 'Pay rate', 'Pay type', 'Home Email', 'Mobile Phone', 'Location', 'Division', 'Job Title', 'Time Zone'],
        row: ['', 'Avery', 'Johnson', '123 Main St', 'Suite 100', 'Clearwater', 'FL', '33602', '25.00', 'Hourly', 'avery.johnson@example.com', '555-010-5555', 'Wright of Florida', 'Clearwater', 'Traffic Controller', 'Eastern Standard Time'],
    },
    EQUIPMENTS: {
        fileName: 'equipment-import-sample.csv',
        headers: ['Name', 'SerialNumber', 'ExternalId', 'BranchName', 'EquipmentType', 'IsAFAD', 'IsGeneric'],
        row: ['Arrow Board 01', 'AB-001', 'EQ-001', 'Tampa Branch', 'Arrow Board', '0', 'false'],
    },
    INVENTORY_ITEMS: {
        fileName: 'inventory-items-import-sample.csv',
        headers: ['Name', 'ExternalId', 'SalesAccount'],
        row: ['Traffic Cone', 'INV-001', 'Sales'],
    },
    POSITIONS: {
        fileName: 'positions-import-sample.csv',
        headers: ['Position', 'Role'],
        row: ['Flagger', 'Employee'],
    },
    VEHICLES: {
        fileName: 'vehicles-import-sample.csv',
        headers: ['Custref', 'Location', 'SerialNumber'],
        row: ['Truck 01', 'Tampa Branch', 'TRK-001'],
    },
};

const escapeCsvValue = (value) => {
    const text = `${value ?? ''}`;
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const downloadSampleDocument = (mode) => {
    const sample = sampleDocuments[mode];
    if (!sample) return;

    const content = [
        sample.headers.map(escapeCsvValue).join(','),
        sample.row.map(escapeCsvValue).join(','),
    ].join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = sample.fileName;
    link.click();
    URL.revokeObjectURL(url);
};

/**
 * Shared class component for importing customer data from a CSV.
 * Rendered inside the Support Tools page (see supportTools.registry.js).
 *
 * Props:
 * @param {function} onSuccess - Callback after submission (e.g., refreshUsageHistory)
 */
class ImportCustomerDataForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            saving: false,
            tenants: [],
            selectedTenantOption: null,
            selectedModeOption: null,
            selectedFile: null,
        };
    }

    componentDidMount() {
        this.loadTenants();
    }

    loadTenants = async () => {
        try {
            const tenants = await httpClient.js(ApiRoutes.typeAheads.tenants());
            this.setState({ tenants });
        } catch (err) {
            console.error(err);
        }
    };

    onSubmit = async (e) => {
        if (e) e.preventDefault();

        const { selectedTenantOption, selectedModeOption, selectedFile } = this.state;

        if (!selectedTenantOption) {
            toasty.error('Please select a tenant.');
            return;
        }

        if (!selectedModeOption) {
            toasty.error('Please select an import type.');
            return;
        }

        if (!selectedFile) {
            toasty.error('Please attach a .csv file.');
            return;
        }

        this.setState({ saving: true });

        try {
            const response = await httpClient.postFile(
                ApiRoutes.adminTool.importCustomerData(),
                {
                    tenantId: selectedTenantOption.value,
                    mode: selectedModeOption.value,
                    file: selectedFile,
                },
            );

            if (response.status === 400) {
                toasty.error('Error importing customer data. Please check your upload file and try again.');
            } else {
                this.setState({
                    selectedTenantOption: null,
                    selectedModeOption: null,
                    selectedFile: null,
                });
                toasty.success('Customer data imported successfully!');
            }

            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } catch (err) {
            console.error(err);
            toasty.error('Customer data failed to upload');

            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } finally {
            this.setState({ saving: false });
        }
    };

    render() {
        const {
            saving, tenants, selectedTenantOption, selectedModeOption, selectedFile,
        } = this.state;

        const isSubmitDisabled = saving || !selectedTenantOption || !selectedModeOption || !selectedFile;

        return (
            <form onSubmit={this.onSubmit}>
                <div className="d-flex justify-content-end mb-3">
                    <Link
                        component={SmallButton}
                        type="button"
                        to={AppNavPaths.Tenants}
                    >
                        <FontAwesomeIcon
                            icon={faWarehouse}
                            className="mr-1"
                        />
                        Go to Tenants
                    </Link>
                </div>

                <ValidatedSelect
                    id="selectedTenant"
                    name="tenantId"
                    label="Select a tenant"
                    required
                    options={tenants}
                    value={selectedTenantOption}
                    onChange={(option) => this.setState({ selectedTenantOption: option })}
                    error="Select a tenant to update"
                />

                <ValidatedSelect
                    id="importOption"
                    name="mode"
                    label="What kind of data are you importing?"
                    required
                    options={importOptions}
                    value={selectedModeOption}
                    onChange={(option) => this.setState({ selectedModeOption: option })}
                    error="Select import behaviour"
                />

                <FormGroup>
                    <FormLabel label="Sample Document" />
                    <div>
                        <SmallButton
                            type="button"
                            disabled={!selectedModeOption}
                            onClick={() => downloadSampleDocument(selectedModeOption?.value)}
                        >
                            <FontAwesomeIcon
                                icon={faDownload}
                                className="mr-1"
                            />
                            Download Sample CSV
                        </SmallButton>
                    </div>
                </FormGroup>

                <FormGroup>
                    <FormLabel
                        label="Attach File (.csv)"
                        required
                    />
                    <Upload
                        onDrop={([file]) => this.setState({ selectedFile: file })}
                    />
                </FormGroup>

                <BlockBorder />

                <div className="d-flex justify-content-center">
                    <SaveButton
                        saving={saving}
                        disabled={isSubmitDisabled}
                        label="Submit"
                    />
                </div>
            </form>
        );
    }
}

/**
 * Renders the execution-history details for an Import Customer Data run.
 */
export const ImportCustomerDataHistoryDetails = ({ record }) => (
    <List type="unstyled">
        {record.input.TenantId && (
            <li>
                <strong>Tenant ID:</strong>
                {' '}
                {record.input.TenantId}
            </li>
        )}
        {record.input.Mode && (
            <li>
                <strong>Import Type:</strong>
                {' '}
                {record.input.Mode}
            </li>
        )}
        {record.input.FileName && (
            <li>
                <strong>File:</strong>
                <SmallButton
                    onClick={() => {
                        const url = ApiRoutes.adminTool.getImportFile(record.id);
                        httpClient.downloadFile(url, null, record.input.FileName);
                    }}
                >
                    <FontAwesomeIcon
                        icon={faDownload}
                        color="text-primary"
                        className="mr-1"
                    />
                    {record.input.FileName}
                </SmallButton>
            </li>
        )}
        {Boolean(record.output.Succeeded) && (
            <li>
                <strong>Succeeded:</strong>
                {' '}
                {record.output.Succeeded}
            </li>
        )}
        {Boolean(record.output.Failed) && (
            <li>
                <strong>Failed:</strong>
                {' '}
                {record.output.Failed}
            </li>
        )}
    </List>
);

export { ImportCustomerDataForm };
export default ImportCustomerDataForm;
